import { useEffect, useMemo, useState } from "react";
import type { Book } from "../api/types";
import Modal from "./Modal";

/**
 * Picks the books a chat may draw on. Categories are the durable way to group
 * a library; this is for the question you want to ask right now, of three
 * particular papers.
 */
export default function BookPickerDialog({
  open,
  books,
  selected,
  onClose,
  onConfirm,
}: {
  open: boolean;
  books: Book[];
  selected: string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(selected);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (open) {
      setChosen(selected);
      setFilter("");
    }
    // `selected` is read only when the dialog opens; editing is local until confirmed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return books;
    return books.filter((book) =>
      [book.book_title, book.video_title, book.channel]
        .filter(Boolean)
        .some((field) => (field as string).toLowerCase().includes(needle))
    );
  }, [books, filter]);

  function toggle(id: string) {
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <Modal open={open} onClose={onClose} title="Choose books to talk to">
      <div className="space-y-3">
        {books.length > 6 ? (
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title or channel…"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted"
          />
        ) : null}

        <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-border p-1">
          {visible.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted">
              {books.length === 0
                ? "No finished papers yet."
                : "Nothing matches that filter."}
            </p>
          ) : (
            visible.map((book) => (
              <label
                key={book.id}
                className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-bg"
              >
                <input
                  type="checkbox"
                  checked={chosen.includes(book.id)}
                  onChange={() => toggle(book.id)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-accent"
                />
                <span className="min-w-0">
                  <span className="block truncate text-text">
                    {book.book_title ?? book.video_title ?? "Untitled"}
                  </span>
                  {book.channel ? (
                    <span className="block truncate text-xs text-muted">{book.channel}</span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted">
            {chosen.length === 0
              ? "Nothing selected"
              : `${chosen.length} of ${books.length} selected`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-bg"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={chosen.length === 0}
              onClick={() => onConfirm(chosen)}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
            >
              Use these
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
